import type { QueryClient, QueryKey } from '@tanstack/react-query'
import { API_BASE_URL } from './client'
import type { Job, JobType } from './types'
import { pageVisible } from '../state/use-page-visible'

const JOB_EVENT = 'job.updated'
const NOTIFICATION_EVENT = 'notification.created'
const DEFAULT_BATCH_WINDOW_MS = 5_000
const DEFAULT_RECONNECT_INITIAL_MS = 1_000
const DEFAULT_RECONNECT_MAX_MS = 30_000
const DEFAULT_STABLE_CONNECTION_MS = 15_000

const jobsKey = ['jobs'] as const
const jobKey = (id: string) => ['job', id] as const
const dashboardKey = ['dashboard'] as const
const productsKey = ['products'] as const
const productPageKey = ['product-page'] as const
const productProgressKey = ['product-pool-progress'] as const
const productKey = ['product'] as const
const offersKey = ['offers'] as const
const offerKey = ['offer'] as const
const listingGroupsKey = ['listing-groups'] as const
const validationKey = ['validation'] as const
const ordersKey = ['orders'] as const
const orderKey = ['order'] as const
const shopsKey = ['shops'] as const
const notificationsKey = ['notifications'] as const
const aiSettingsKey = ['ai-settings'] as const

const reconnectKeys: readonly QueryKey[] = [
  jobsKey,
  dashboardKey,
  productsKey,
  productPageKey,
  productProgressKey,
  productKey,
  offersKey,
  offerKey,
  listingGroupsKey,
  ordersKey,
  orderKey,
  shopsKey,
  notificationsKey,
  aiSettingsKey,
]

type TenantEventName = typeof JOB_EVENT | typeof NOTIFICATION_EVENT

interface EventSourceLike {
  addEventListener(type: string, listener: EventListener): void
  removeEventListener(type: string, listener: EventListener): void
  close(): void
}

interface EventSourceBinding {
  source: EventSourceLike
  listeners: ReadonlyArray<readonly [string, EventListener]>
}

export type TenantEventSourceFactory = (url: string, init: EventSourceInit) => EventSourceLike

interface TenantEventsControllerOptions {
  queryClient: Pick<QueryClient, 'invalidateQueries'>
  eventSourceFactory?: TenantEventSourceFactory
  url?: string
  batchWindowMs?: number
  reconnectInitialMs?: number
  reconnectMaxMs?: number
  stableConnectionMs?: number
  reconnectJitterRatio?: number
  random?: () => number
}

function parseEventData(data: unknown): unknown {
  if (typeof data !== 'string') return data
  try {
    return JSON.parse(data) as unknown
  } catch {
    return data
  }
}

function jobIdentity(data: unknown): Pick<Job, 'id' | 'type'> | null {
  if (!data || typeof data !== 'object') return null
  const candidate = data as Partial<Job>
  if (typeof candidate.id !== 'string' || typeof candidate.type !== 'string') return null
  return { id: candidate.id, type: candidate.type as JobType }
}

function keysForJobType(type: JobType | undefined): readonly QueryKey[] {
  switch (type) {
    case 'COLLECT':
      return [productsKey, productPageKey, productProgressKey, productKey, offersKey, offerKey, listingGroupsKey, validationKey]
    case 'GENERATE_LISTING':
      return [productsKey, productKey, offersKey, offerKey, listingGroupsKey, validationKey]
    case 'PUBLISH':
    case 'RECONCILE':
      return [productsKey, productKey, offersKey, offerKey, listingGroupsKey, validationKey]
    case 'ORDER_SYNC':
      return [ordersKey, orderKey]
    case 'CREDENTIAL_VERIFY':
      return [shopsKey]
    case 'AI_CONNECTION_TEST':
      return [aiSettingsKey]
    default:
      return []
  }
}

export function buildTenantEventsUrl(baseUrl = API_BASE_URL): string {
  return `${baseUrl.replace(/\/+$/, '')}/events`
}

export function invalidationKeysForTenantEvent(
  eventName: TenantEventName,
  rawData: unknown,
): readonly QueryKey[] {
  if (eventName === NOTIFICATION_EVENT) return [notificationsKey]

  const job = jobIdentity(parseEventData(rawData))
  return [jobsKey, dashboardKey, ...(job ? [jobKey(job.id)] : []), ...keysForJobType(job?.type)]
}

function browserEventSourceFactory(url: string, init: EventSourceInit): EventSourceLike {
  return new EventSource(url, init)
}

export class TenantEventsController {
  private readonly queryClient: Pick<QueryClient, 'invalidateQueries'>
  private readonly eventSourceFactory: TenantEventSourceFactory
  private readonly url: string
  private readonly batchWindowMs: number
  private readonly reconnectInitialMs: number
  private readonly reconnectMaxMs: number
  private readonly stableConnectionMs: number
  private readonly reconnectJitterRatio: number
  private readonly random: () => number
  private readonly pendingInvalidations = new Map<string, QueryKey>()
  private lastProductPageRefresh = 0

  private running = false
  private hasConnected = false
  private reconnectAttempt = 0
  private binding: EventSourceBinding | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private stableTimer: ReturnType<typeof setTimeout> | null = null
  private batchTimer: ReturnType<typeof setTimeout> | null = null

  constructor(options: TenantEventsControllerOptions) {
    this.queryClient = options.queryClient
    this.eventSourceFactory = options.eventSourceFactory ?? browserEventSourceFactory
    this.url = options.url ?? buildTenantEventsUrl()
    this.batchWindowMs = options.batchWindowMs ?? DEFAULT_BATCH_WINDOW_MS
    this.reconnectInitialMs = options.reconnectInitialMs ?? DEFAULT_RECONNECT_INITIAL_MS
    this.reconnectMaxMs = options.reconnectMaxMs ?? DEFAULT_RECONNECT_MAX_MS
    this.stableConnectionMs = options.stableConnectionMs ?? DEFAULT_STABLE_CONNECTION_MS
    this.reconnectJitterRatio = Math.max(0, Math.min(1, options.reconnectJitterRatio ?? 0.2))
    this.random = options.random ?? Math.random
  }

  start(): void {
    if (this.running) return
    this.running = true
    this.connect()
  }

  stop(): void {
    this.running = false
    this.hasConnected = false
    this.reconnectAttempt = 0
    this.clearReconnectTimer()
    this.clearStableTimer()
    this.clearBatchTimer()
    this.pendingInvalidations.clear()
    this.closeCurrentSource()
  }

  private connect(): void {
    if (!this.running || this.binding) return

    let source: EventSourceLike
    try {
      source = this.eventSourceFactory(this.url, { withCredentials: true })
    } catch {
      this.scheduleReconnect()
      return
    }

    const openListener: EventListener = () => {
      if (!this.running || this.binding?.source !== source) return
      if (this.hasConnected) this.queueInvalidations(reconnectKeys)
      this.hasConnected = true
      this.clearStableTimer()
      this.stableTimer = setTimeout(() => {
        if (this.running && this.binding?.source === source) this.reconnectAttempt = 0
      }, this.stableConnectionMs)
    }
    const errorListener: EventListener = () => {
      if (!this.running || this.binding?.source !== source) return
      this.closeCurrentSource()
      this.clearStableTimer()
      this.scheduleReconnect()
    }
    const jobListener: EventListener = (event) => {
      this.handleServerEvent(JOB_EVENT, (event as MessageEvent<unknown>).data)
    }
    const notificationListener: EventListener = (event) => {
      this.handleServerEvent(NOTIFICATION_EVENT, (event as MessageEvent<unknown>).data)
    }
    const listeners: EventSourceBinding['listeners'] = [
      ['open', openListener],
      ['error', errorListener],
      [JOB_EVENT, jobListener],
      [NOTIFICATION_EVENT, notificationListener],
    ]

    listeners.forEach(([type, listener]) => source.addEventListener(type, listener))
    this.binding = { source, listeners }
  }

  private handleServerEvent(eventName: TenantEventName, data: unknown): void {
    if (!this.running) return
    this.queueInvalidations(invalidationKeysForTenantEvent(eventName, data))
  }

  private queueInvalidations(keys: readonly QueryKey[]): void {
    keys.forEach((key) => this.pendingInvalidations.set(JSON.stringify(key), key))
    if (this.batchTimer) return
    this.batchTimer = setTimeout(() => {
      this.batchTimer = null
      const queued = [...this.pendingInvalidations.values()]
      this.pendingInvalidations.clear()
      void Promise.all(
        queued.map((queryKey) => {
          const page = queryKey[0] === 'product-page'
          if (page && Date.now() - this.lastProductPageRefresh < 15_000) {
            this.queueInvalidations([queryKey])
            return Promise.resolve()
          }
          if (page) this.lastProductPageRefresh = Date.now()
          return this.queryClient.invalidateQueries({ queryKey, refetchType: pageVisible() ? 'active' : 'none' })
        }),
      ).catch(() => undefined)
    }, this.batchWindowMs)
  }

  private scheduleReconnect(): void {
    if (!this.running || this.reconnectTimer) return
    const exponentialDelay = Math.min(
      this.reconnectMaxMs,
      this.reconnectInitialMs * 2 ** this.reconnectAttempt,
    )
    this.reconnectAttempt += 1
    const jitterMultiplier = 1 + (this.random() * 2 - 1) * this.reconnectJitterRatio
    const delay = Math.max(0, Math.round(exponentialDelay * jitterMultiplier))
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.connect()
    }, delay)
  }

  private closeCurrentSource(): void {
    if (!this.binding) return
    const { source, listeners } = this.binding
    this.binding = null
    listeners.forEach(([type, listener]) => source.removeEventListener(type, listener))
    source.close()
  }

  private clearReconnectTimer(): void {
    if (!this.reconnectTimer) return
    clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
  }

  private clearStableTimer(): void {
    if (!this.stableTimer) return
    clearTimeout(this.stableTimer)
    this.stableTimer = null
  }

  private clearBatchTimer(): void {
    if (!this.batchTimer) return
    clearTimeout(this.batchTimer)
    this.batchTimer = null
  }
}
